import React, {useCallback, useEffect} from 'react';
import {BrowserRouter as Router, Routes, Route, useLocation, Navigate} from "react-router-dom";
import {Provider, useDispatch} from 'react-redux';
import {PersistGate} from "redux-persist/integration/react";
import {store, persistor} from "./store/store";
import {Layout} from "antd";
import Sidebar from "./components/AfSideBar";
import HeaderComponent from "./components/AfHeader";
import Home from "./pages/Home";
import PersonRegisterForm from "./pages/PersonRegisterForm";
import theme from "./theme";
import PeoplePage from "./pages/PeoplePage";
import {ConfigProvider} from 'antd';
import esES from 'antd/locale/es_ES';
import AfNavigation from "./components/AfNavigation";
import LoginPage from './pages/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import {logout} from './store/reducers/authReducer';
import socket from './services/socketService';
import {useMediaQuery} from "react-responsive";
import useIsLoggedIn, {
    COT_ROLE,
    DISTRICT_ROLE,
    NATIONAL_ROLE,
    PROMOTER_ROLE,
    QUERY_STATE_ROLE,
    SECTIONAL_ROLE,
    STATE_ROLE
} from "./utils/userSessionUtils";
import PromotersPage from "./pages/PromotersPage";
import StatisticsPage from "./pages/StatisticsPage";
import StatisticsPromotersPage from "./pages/StatisticsPromotersPage";
import PeoplePageAsUser from "./pages/PeoplePageAsUser";
import PaymentInformationForm from "./pages/PaymentInformationForm";
import PaymentInformationPageAsUser from "./pages/PaymentInformationFormAsUser";
import SectionalsPage from "./pages/SectionalsPage";
import PromoterRegisterPage from "./pages/PromoterRegisterPage";
import DirectorySurveyPage from "./pages/DirectorySurveyPage";
import PromocionPage from "./pages/PromocionPage";
import PaseDeListaPage from "./pages/PaseDeListaPage";
import PaseDeListaAfiliadosPage from "./pages/PaseDeListaAfiliadosPage";
import AvanceAsistenciaPage from "./pages/AvanceAsistenciaPage";

const {Content} = Layout;

const AppContent = () => {
    const isLoggedIn = useIsLoggedIn();
    const location = useLocation();
    const dispatch = useDispatch();
    const isMobile = useMediaQuery({maxWidth: 768});
    const isLoginPage = location.pathname === "/login";

    const handleLogout = useCallback(() => {
        dispatch(logout());
    }, [dispatch]);


    useEffect(() => {
        if (!isLoginPage && !isLoggedIn) {
            handleLogout();
        }
    }, [isLoginPage, isLoggedIn, handleLogout]);

    useEffect(() => {
        if (isLoggedIn) {
            socket.connect();
        } else {
            socket.disconnect();
        }
    }, [isLoggedIn]);

    // Put this to true to enable the under construction page
    // return <UnderConstruction />;

    if (!isLoginPage && !isLoggedIn) {
        return <Navigate to="/login"/>;
    }

    if (isLoginPage && isLoggedIn) {
        return <Navigate to="/"/>;
    }

    return (
        <ConfigProvider 
            locale={esES}
            theme={{
                token: {
                    colorPrimary: theme.colors.primary,
                },
            }}
        >
            <Layout style={{minHeight: "100vh"}}>
                {isLoggedIn && <Sidebar/>}
                {isLoggedIn && <AfNavigation/>}
                {isLoggedIn && <HeaderComponent onLogout={handleLogout}/>}
                <Content style={{
                    padding: !isLoggedIn ? "0" : "20px",
                    background: theme.colors.background,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: !isLoggedIn ? "center" : "flex-start",
                    marginTop: !isLoggedIn ? "0" : isMobile ? "110px" : "120px",
                    paddingBottom: isLoggedIn && isMobile ? "80px" : "0"
                }}>
                    <Routes>
                        <Route path="/login" element={<LoginPage/>}/>
                        <Route path="/" element={<ProtectedRoute allowedRoles={"*"}><Home/></ProtectedRoute>}/>
                        <Route path="/registro" element={<ProtectedRoute allowedRoles={[SECTIONAL_ROLE, PROMOTER_ROLE, COT_ROLE]}><PersonRegisterForm/></ProtectedRoute>}/>
                        <Route path="/personas" element={<ProtectedRoute allowedRoles={[SECTIONAL_ROLE, PROMOTER_ROLE, COT_ROLE]}><PeoplePage/></ProtectedRoute>}/>
                        <Route path="/promotores" element={<ProtectedRoute allowedRoles={[DISTRICT_ROLE, COT_ROLE, SECTIONAL_ROLE]}><PromotersPage/></ProtectedRoute>}/>
                        <Route path="/promotores/nuevo" element={<ProtectedRoute allowedRoles={[SECTIONAL_ROLE, COT_ROLE, DISTRICT_ROLE]}><PromoterRegisterPage/></ProtectedRoute>}/>
                        <Route path="/seccionales" element={<ProtectedRoute allowedRoles={[DISTRICT_ROLE, COT_ROLE, SECTIONAL_ROLE]}><SectionalsPage/></ProtectedRoute>}/>
                        <Route path="/metas-dashboard" element={<ProtectedRoute allowedRoles={[QUERY_STATE_ROLE, STATE_ROLE, DISTRICT_ROLE, COT_ROLE, SECTIONAL_ROLE]}><StatisticsPage/></ProtectedRoute>}/>
                        <Route path="/metas-promotores-dashboard" element={<ProtectedRoute allowedRoles={[QUERY_STATE_ROLE, STATE_ROLE, DISTRICT_ROLE, COT_ROLE, SECTIONAL_ROLE]}><StatisticsPromotersPage/></ProtectedRoute>}/>
                        <Route path="/people-as-user/:username/:from" element={<ProtectedRoute allowedRoles={[DISTRICT_ROLE, COT_ROLE, SECTIONAL_ROLE]}><PeoplePageAsUser/></ProtectedRoute>}/>
                        <Route path="/informacion-nomina" element={<ProtectedRoute allowedRoles={[SECTIONAL_ROLE]}><PaymentInformationForm/></ProtectedRoute>}/>
                        <Route path="/informacion-nomina/:username" element={<ProtectedRoute allowedRoles={[DISTRICT_ROLE, COT_ROLE]}><PaymentInformationPageAsUser/></ProtectedRoute>}/>
                        {/* <Route path="/directory" element={<ProtectedRoute allowedRoles={[SECTIONAL_ROLE]}><DirectoryPage/></ProtectedRoute>}/> */}
                        <Route path="/directory-survey" element={<ProtectedRoute allowedRoles={[SECTIONAL_ROLE, PROMOTER_ROLE, COT_ROLE, DISTRICT_ROLE, STATE_ROLE, QUERY_STATE_ROLE]}><DirectorySurveyPage/></ProtectedRoute>}/>
                        <Route path="/reporte-promocion" element={<ProtectedRoute allowedRoles={[DISTRICT_ROLE, PROMOTER_ROLE, SECTIONAL_ROLE, COT_ROLE, STATE_ROLE, QUERY_STATE_ROLE]}><PromocionPage/></ProtectedRoute>}/>
                        <Route path="/pase-de-lista" element={<ProtectedRoute allowedRoles={[PROMOTER_ROLE, SECTIONAL_ROLE, COT_ROLE, DISTRICT_ROLE, STATE_ROLE]}><PaseDeListaPage/></ProtectedRoute>}/>
                        <Route path="/afiliados" element={<ProtectedRoute allowedRoles={[PROMOTER_ROLE, SECTIONAL_ROLE, COT_ROLE, DISTRICT_ROLE, STATE_ROLE]}><PaseDeListaAfiliadosPage/></ProtectedRoute>}/>
                        <Route path="/avance-asistencia" element={<ProtectedRoute allowedRoles={[DISTRICT_ROLE, STATE_ROLE, QUERY_STATE_ROLE, NATIONAL_ROLE]}><AvanceAsistenciaPage/></ProtectedRoute>}/>
                    </Routes>
                </Content>
            </Layout>
        </ConfigProvider>
    );
};

const App = () => {
    return (
        <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
                <Router>
                    <AppContent/>
                </Router>
            </PersistGate>
        </Provider>
    );
};

export default App;
